import {
  dateToEpochSecondsOptional,
  epochSecondsToDateOptional,
  stripUndefined,
  z,
} from '@customerio/hydra-validation';
import type {
  ActionFromUI as UIAPIActionFromUI,
  ActionToUI as UIAPIActionToUI,
  CampaignsCampaignModel,
} from '@customerio/hydra-ui-api';
import { deleteCrossAppCacheKeys } from '@/utils/cross-app-cache';
import { defineEntity, readSchema, writeSchema } from '../shared/define-entity';

/**
 * Action entity.
 *
 * `Action` is the generated ActionToUI wire shape with epoch-second
 * date fields swapped for `Date` instances. The generated schema models
 * the full detail response, but this cache layer also stores sparse rows
 * from campaign-list sideloads, so most fields intentionally remain
 * optional on the entity type.
 *
 * Mirrors the role of `services/db/collections/action.ts`: superset of
 * the sparse `ActionForCampaignsList` (campaigns sideload) and the full
 * `ActionToUI` (GET /actions/:id and mutations). Detail-only fields are
 * `undefined` on rows that arrived via sideload; mutations promote
 * summary→detail automatically since merged-into-cache writes the new
 * fields and leaves the rest alone.
 */

/* ------------------------------------------------------------------------- */
/* Type                                                                       */
/* ------------------------------------------------------------------------- */

/**
 * `subscription_topic_id` is normalized to `string` here even though the
 * wire ships it as `number | null` — mirrors the Tag/SubscriptionTopic
 * coercion convention so an action→subscription_topic FK follow can hit
 * the string-keyed `SubscriptionTopic` detail cache without per-call
 * `.toString()` plumbing. Same direction at write time.
 */
type ActionWire = Partial<Omit<UIAPIActionToUI, 'position' | 'type'>> & {
  id: string;
  position?: UIAPIActionToUI['position'] | null;
  type: string;
};

export type Action = Omit<ActionWire, 'created' | 'updated' | 'started' | 'subscription_topic_id'> & {
  created?: Date;
  updated?: Date;
  started?: Date;
  subscription_topic_id?: string | null;
};

type ActionWriteInput = Partial<Action>;
type ActionWireWritePayload = Partial<UIAPIActionFromUI>;

/* ------------------------------------------------------------------------- */
/* Schemas — only the transforms                                              */
/* ------------------------------------------------------------------------- */

const ActionFromApi = readSchema<Action>(
  z.looseObject({
    created: epochSecondsToDateOptional,
    updated: epochSecondsToDateOptional,
    started: epochSecondsToDateOptional,
    subscription_topic_id: z
      .number()
      .nullish()
      .transform((n) => (n == null ? n : String(n))),
  }),
);

/** The inverse of the read transform above: the string key back to the wire's number. */
const SubscriptionTopicIdToApi = z
  .string()
  .nullish()
  .transform((s) => (s == null ? s : Number(s)))
  .pipe(z.number().finite().nullish());

export const ActionToApi = writeSchema<ActionWriteInput, ActionWireWritePayload>(
  z
    .looseObject({
      created: dateToEpochSecondsOptional,
      updated: dateToEpochSecondsOptional,
      started: dateToEpochSecondsOptional,
      subscription_topic_id: SubscriptionTopicIdToApi,
    })
    .transform(stripUndefined),
);

/* ------------------------------------------------------------------------- */
/* Entity                                                                     */
/* ------------------------------------------------------------------------- */

type ActionRelations = {
  readonly campaign: {
    readonly kind: 'belongs-to';
    readonly target: 'campaign';
    readonly via: 'campaign_id';
  };
};

/** @public */
export const action = defineEntity<Action, ActionWireWritePayload, void, ActionRelations, 'workspace'>({
  name: 'action',
  scope: 'workspace',
  pk: (a) => a.id,
  schema: { read: ActionFromApi, write: ActionToApi },
  relations: {
    campaign: { kind: 'belongs-to', target: 'campaign', via: 'campaign_id' },
  },
});

export const actionKeys = action.keys;

/**
 * The cross-app cache key for an action's `GET /actions/:id` response. Ember's
 * action adapter reads the same key, so its composer fallback can skip the
 * request Hydra just made. The environment prefix keeps two workspaces' actions
 * apart during a workspace switch.
 */
export function actionCacheKey(workspaceId: number, actionId: string) {
  return `/environments/${workspaceId}/actions/${actionId}`;
}

/**
 * Drops the cached responses for actions a write changed, so Ember never reads
 * a pre-save action. Exact keys, not a prefix: the environment prefix covers
 * entries no action write touches.
 */
export function dropCachedActions(workspaceId: number, actionIds: Iterable<string>) {
  deleteCrossAppCacheKeys(...Array.from(actionIds, (id) => actionCacheKey(workspaceId, id)));
}

/**
 * The actions a write that returns a campaign model changed: the ones the
 * request named, every action the response returns, and each returned split's
 * variants (`action_ids`). A variant the response leaves out can still hold a
 * cached read from before the write changed it.
 */
export function campaignModelActionIds(
  model: { actions?: CampaignsCampaignModel['actions'] },
  requested: readonly string[],
): string[] {
  return [...requested, ...(model.actions ?? []).flatMap((changed) => [changed.id, ...(changed.action_ids ?? [])])];
}

export function parseAction<T extends { id: string; type: string }>(raw: T): Action {
  return action.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Computed properties — selectors as plain functions                         */
/* ------------------------------------------------------------------------- */

const EMAIL_TYPES = new Set(['email_action', 'transactional_email', 'newsletter_email']);

/** @public */
export const isEmail = (a: Action) => EMAIL_TYPES.has(a.type);
/** @public */
export const isExit = (a: Action) => a.type === 'exit_action';
/** @public */
export const isDelay = (a: Action) =>
  a.type === 'time_delay_action' || a.type === 'time_window_action' || a.type === 'conditional_wait_action';
/** @public */
export const isSplitTest = (a: Action) => a.type === 'split_test_action';
/** @public */
export const isBranch = (a: Action) =>
  a.type === 'multi_conditional_branch_action' || a.type === 'true_false_branch_action';
/** @public */
export const isDraft = (a: Action) => a.action_state === 'draft';

/* ------------------------------------------------------------------------- */
/* Write-side field coercion for non-entity payloads                          */
/* ------------------------------------------------------------------------- */

/**
 * The action fields {@link ActionFromApi} retypes on read, as the entity holds
 * them. A payload built from a fetched action carries these in the entity's
 * types, not the wire's, so it must undo them before it is sent.
 */
export type ActionReadTransformedFields = Pick<Action, 'created' | 'updated' | 'started' | 'subscription_topic_id'>;

/** The same fields in the types `ActionFromUI` declares. */
type ActionWriteTransformedFields = Pick<UIAPIActionFromUI, 'subscription_topic_id'> & {
  created?: number;
  updated?: number;
  started?: number;
};

/**
 * Undoes every read transform on a payload assembled from fetched actions.
 *
 * Bodies that go out through {@link action}'s write schema get this for free.
 * A caller that assembles its own body — the workflow builder's `add_actions`
 * request builds one action payload per copied node — does not, so it routes
 * the payload through here instead: the read transforms are listed in exactly
 * one place, and a field added to {@link ActionFromApi} later is undone on
 * both paths at once.
 *
 * Fields the payload does not carry stay absent, so this never introduces a
 * key the request did not already have.
 */
export function toActionWriteFields<T extends Partial<ActionReadTransformedFields>>(
  payload: T,
): Omit<T, keyof ActionReadTransformedFields> & Partial<ActionWriteTransformedFields> {
  const { created, updated, started, subscription_topic_id: subscriptionTopicId, ...rest } = payload;
  return {
    ...rest,
    ...stripUndefined({
      created: dateToEpochSecondsOptional.parse(created),
      updated: dateToEpochSecondsOptional.parse(updated),
      started: dateToEpochSecondsOptional.parse(started),
      subscription_topic_id: SubscriptionTopicIdToApi.parse(subscriptionTopicId),
    }),
  };
}
